import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { SavedPosCartsIndexTable } from '@/components/pos/SavedPosCartsIndexTable'
import { IndexTableSearchBox, IndexTableToolbar } from '@/components/tables/IndexTable'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import { usePathHelper } from '@/hooks/usePathHelper'
import { cartQueries, useSavedPosCarts } from '@/lib/services/carts/carts.query'
import {
  defaultSavedPosCartSearchParams,
  getSavedPosCartFilters,
  savedPosCartSearchSchema,
} from '@/lib/services/carts/carts.schema'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import {
  createFileRoute,
  Link,
  notFound,
  redirect,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import { Bookmark } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/$orgId/sales-orders/drafts')({
  component: DraftOrdersPage,
  validateSearch: (search) => savedPosCartSearchSchema.parse(search),
  search: {
    middlewares: [stripSearchParams(defaultSavedPosCartSearchParams)],
  },
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, deps: { search }, params }) => {
    if (!context.ability.can('create', 'sales-orders')) throw notFound()

    // Draft orders belong to one shop's POS; the global organization has none to show.
    if (+params.orgId === +import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID) {
      throw redirect({ to: '/$orgId/sales-orders', params: { orgId: params.orgId } })
    }

    void prefetchIndexTableRouteQuery(
      context.queryClient,
      cartQueries.savedPos({
        organizationId: params.orgId,
        filters: getSavedPosCartFilters(search),
        page: search.page,
        pageSize: search.page_size,
      })
    )
  },
})

function draftOrderCountText(total) {
  return `${total} draft order${total === 1 ? '' : 's'}`
}

function DraftOrdersPage() {
  const { orgId } = Route.useParams()
  // Already validated by the route, so page and page_size are always present.
  const urlSearch = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const path = usePathHelper()
  const {
    data: response,
    isPending,
    isFetching,
  } = useSavedPosCarts({
    organizationId: orgId,
    filters: getSavedPosCartFilters(urlSearch),
    page: urlSearch.page,
    pageSize: urlSearch.page_size,
  })
  const savedCarts = response?.data ?? []

  const { updateSearchTerm, updatePage, updatePageSize, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: (previous) => previous,
      searchTerm: urlSearch.search ?? '',
    })

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Bookmark}
        title="Draft Orders"
        description="Orders your team started on the POS and saved as drafts to finish later."
        actions={
          <CreateActionButton asChild>
            <Link {...path.salesOrders.link('create')}>Create Order</Link>
          </CreateActionButton>
        }
      />

      <SavedPosCartsIndexTable
        orgId={orgId}
        savedCarts={savedCarts}
        response={response}
        page={urlSearch.page}
        pageSize={urlSearch.page_size}
        onPageChange={updatePage}
        onPageSizeChange={updatePageSize}
        isLoading={isPending}
        toolbar={
          <IndexTableToolbar
            primary={
              <IndexTableSearchBox
                value={urlSearch.search ?? ''}
                onCommit={updateSearchTerm}
                countText={draftOrderCountText(response?.meta?.total ?? savedCarts.length)}
                isFetching={isFetching}
                placeholder="Search drafts..."
              />
            }
            secondary={
              <Select
                value={urlSearch.payment}
                onValueChange={(payment) => updateTableParams({ payment })}
              >
                <SelectTrigger
                  aria-label="Filter drafts by payment"
                  selected={urlSearch.payment !== 'all'}
                  className="w-fit"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All drafts</SelectItem>
                  <SelectItem value="held">With payment</SelectItem>
                </SelectContent>
              </Select>
            }
          />
        }
      />
    </div>
  )
}
